import React, { useState, useEffect } from "react";
import {
  Table,
  Modal,
  Timeline,
  Button,
  Spin,
  Tag,
  Input,
  Space,
  Select,
  Tabs,
} from "antd";
import {
  getInventarioNoSerial,
  getInventarioNoSerialByProducto,
  getInventarioNoSerialByGrupo,
  getInventarioNoSerialCount,
} from "../../services/inventarioNoSerial";
import { getGruposSinCodebar } from "../../services/grupos";
import {
  getProductos,
  getProductosNoSerializados,
} from "../../services/productos";
import {
  getHistorialDetalleByIdProducto,
  getHistorialSerialesById,
  getHistorialDetalleByIdProductoBatch,
  getHistorialSerialesByIdBatch,
} from "../../services/historial";
import { getInventarioNSPrestamosByID } from "../../services/inventarioNSPrestamos";
import Layouts from "../../components/Layouts";
import { useSelector } from "react-redux";
import validarpermiso from "../../lib/utils";
import PrestamoTimeline from "./components/PrestamoTimeline";
import FacturaList from "./components/FacturaList";

const { Option } = Select;

const InventarioNoSerialTable = () => {
  const [filteredInventario, setFilteredInventario] = useState([]);
  const [historialData, setHistorialData] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasPermissionError, setHasPermissionError] = useState(false);
  const [grupos, setGrupos] = useState([]);
  const [productos, setProductos] = useState([]);
  const [selectedGrupo, setSelectedGrupo] = useState(null);
  const [selectedProducto, setSelectedProducto] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });
  const [facturaData, setFacturaData] = useState([]);

  const permisos = useSelector((state) => state.permisos);

  useEffect(() => {
    const fetchData = async () => {
      if (!validarpermiso("inventario_no_serial", "read", permisos)) {
        setHasPermissionError(true);
        return;
      }

      try {
        const [gruposData, productosData] = await Promise.all([
          getGruposSinCodebar(),
          getProductosNoSerializados(),
        ]);
        setGrupos(gruposData);
        setProductos(productosData);
        fetchTotalCount();
        fetchInventario();
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, [permisos]);

  const fetchTotalCount = async () => {
    try {
      const countResponse = await getInventarioNoSerialCount();
      const total = parseInt(countResponse[0]?.countDistinct?.id || 0);
    } catch (error) {
      console.error("Error fetching total count:", error);
    }
  };

  const fetchInventario = async (
    page = 1,
    pageSize = 25,
    searchValue = "",
    searchType = "all"
  ) => {
    setLoading(true);
    try {
      // Si no se proporcionan searchValue y searchType, calcularlos basados en los estados actuales
      if (!searchValue && !searchType) {
        if (selectedGrupo && selectedProducto) {
          searchType = "both";
          searchValue = `${selectedProducto}|${selectedGrupo}`;
        } else if (selectedGrupo) {
          searchType = "group";
          searchValue = selectedGrupo;
        } else if (selectedProducto) {
          searchType = "product";
          searchValue = selectedProducto;
        }
      }

      let data;
      if (searchType === "both" && searchValue) {
        const [productSearch, groupSearch] = searchValue
          .split("|")
          .map((s) => s.trim());
        const grupoData = await getInventarioNoSerialByGrupo(groupSearch);
        data = grupoData.filter(
          (item) => item.producto?.nombre === productSearch
        );
      } else if (searchType === "group" && searchValue) {
        data = await getInventarioNoSerialByGrupo(searchValue);
      } else if (searchType === "product" && searchValue) {
        data = await getInventarioNoSerialByProducto(searchValue);
      } else {
        data = await getInventarioNoSerial();
      }

      const countResponse = await getInventarioNoSerialCount();
      const filtered = parseInt(countResponse[0]?.countDistinct?.id || 0);

      setFilteredInventario(data);
      setPagination((prev) => ({
        ...prev,
        current: page,
        pageSize,
        total: filtered,
      }));
    } catch (error) {
      console.error("Error fetching inventory:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleGrupoChange = (value) => {
   
    setSelectedGrupo(value);
    setPagination((prev) => ({ ...prev, current: 1 }));

    let searchType = "all";
    let searchValue = "";

    if (value && selectedProducto) {
      searchType = "both";
      searchValue = `${selectedProducto}|${value}`;
    } else if (value) {
      searchType = "group";
      searchValue = value;
    } else if (selectedProducto) {
      searchType = "product";
      searchValue = selectedProducto;
    }

    fetchInventario(1, pagination.pageSize, searchValue, searchType);
  };

  const handleProductoChange = (value) => {

    setSelectedProducto(value);
    setPagination((prev) => ({ ...prev, current: 1 }));

    let searchType = "all";
    let searchValue = "";

    if (value && selectedGrupo) {
      searchType = "both";
      searchValue = `${value}|${selectedGrupo}`;
    } else if (value) {
      searchType = "product";
      searchValue = value;
    } else if (selectedGrupo) {
      searchType = "group";
      searchValue = selectedGrupo;
    }

    fetchInventario(1, pagination.pageSize, searchValue, searchType);
  };

  const fetchHistorial = async (id) => {
    if (!validarpermiso("inventario_no_serial", "read", permisos)) {
      setHasPermissionError(true);
      return;
    }

    setLoading(true);
    try {
      const prestamos = await getInventarioNSPrestamosByID(id);
      const prestamoIds = prestamos.map((prestamo) => prestamo.id);

      // 1. Get all detalle_historial_ns for all prestamos in one call
      const historialDetalles = await getHistorialDetalleByIdProductoBatch(
        prestamoIds
      );

      // 2. Collect all historial_seriales_id from historialDetalles
      const historialSerialesIds = historialDetalles
        .flatMap((item) =>
          item?.historial_seriales && item?.historial_seriales?.length > 0
            ? [item?.historial_seriales[0]?.historial_seriales_id]
            : []
        )
        .filter((id) => id !== null && id !== undefined);

      let historialSerialesMap = {};
      let facturas = [];
      if (historialSerialesIds.length > 0) {
        let historialSerialesArr = await getHistorialSerialesByIdBatch(
          historialSerialesIds
        );

        if (historialSerialesArr && !Array.isArray(historialSerialesArr)) {
          // Si viene null, lo ignoramos
          if (historialSerialesArr.id !== undefined) {
            historialSerialesArr = [historialSerialesArr];
          } else {
            historialSerialesArr = [];
          }
        }

        // Asegúrate de que todos los ids estén presentes y sean válidos
        historialSerialesMap = Object.fromEntries(
          (historialSerialesArr || [])
            .filter((h) => h && h?.id !== undefined && h?.id !== null)
            .map((h) => [h?.id, h])
        );
        // Collect facturas
        facturas = (historialSerialesArr || []).filter((h) => h?.factura_n);
      }

      const historialItems = historialDetalles.map((item) => {
       
        let vendedor = null;
        let serialId =
          item?.historial_seriales && item?.historial_seriales?.length > 0
            ? item?.historial_seriales[0]?.historial_seriales_id
            : null;

        if (serialId && historialSerialesMap[serialId]) {
          // Si el objeto tiene vendedor directo, úsalo
          if (historialSerialesMap[serialId]?.vendedor) {
            vendedor = historialSerialesMap[serialId]?.vendedor;
          } else if (
            historialSerialesMap[serialId]?.prestamo &&
            historialSerialesMap[serialId]?.prestamo?.vendedor
          ) {
            vendedor = historialSerialesMap[serialId]?.prestamo?.vendedor;
          }
        } else {
          vendedor = null;
        }

        
        const prestamo = prestamos.find((p) => p.id === item?.prestamo_ns_p.id);
        
        let precio_producto = null;
        if (prestamo && prestamo?.inventario_ns && prestamo?.inventario_ns?.producto) {
          precio_producto = prestamo?.inventario_ns?.producto?.preciosinimp;
        }
        return {
          ...item,
          vendedor,
          estado: prestamo?.estado,
          fecha: prestamo?.date_created,
          precio_producto,
        };
      });
      historialItems.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
      setHistorialData(historialItems);
      setFacturaData(facturas);
    } catch (error) {
      console.error("Error fetching historial:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerHistorial = (id) => {
    if (!validarpermiso("inventario_no_serial", "read", permisos)) {
      setHasPermissionError(true);
      return;
    }
    fetchHistorial(id);
    setIsModalVisible(true);
  };

  const getTagColor = (estado) => {
    switch (estado) {
      case "devolucion":
        return "orange";
      case "solicitud_prestamo":
        return "green";
      default:
        return "blue";
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Cantidad",
      dataIndex: "cantidad",
      key: "cantidad",
    },
    {
      title: "Producto",
      dataIndex: ["producto", "nombre"],
      key: "producto",
    },
    {
      title: "Acciones",
      key: "actions",
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => handleVerHistorial(record.id)}
          disabled={!validarpermiso("inventario_no_serial", "read", permisos)}
        >
          Ver Historial
        </Button>
      ),
    },
  ];

  return (
    <Layouts>
      {!hasPermissionError ? (
        <>
          <Space style={{ marginBottom: 16 }}>
            <Select
              style={{ width: 200 }}
              placeholder="Seleccionar grupo"
              allowClear
              value={selectedGrupo}
              onChange={handleGrupoChange}
              onClear={() => {
                setSelectedGrupo(null);
                fetchInventario(1, pagination.pageSize);
              }}
              showSearch
              filterOption={false}
              onSearch={(value) => {
                if (value) {
                  const filteredGroups = grupos.filter((grup) =>
                    `${grup.nombre}`.toLowerCase().includes(value.toLowerCase())
                  );
                  setGrupos(filteredGroups);
                } else {
                  getGruposSinCodebar();
                }
              }}
            >
              {grupos.map((grupo) => (
                <Option key={grupo.id} value={grupo.nombre}>
                  {grupo.nombre}
                </Option>
              ))}
            </Select>

            <Select
              style={{ width: 200 }}
              placeholder="Seleccionar producto"
              allowClear
              value={selectedProducto}
              onChange={handleProductoChange}
              onClear={() => {
                setSelectedProducto(null);
                fetchInventario(1, pagination.pageSize);
              }}
              showSearch
              filterOption={false}
              onSearch={(value) => {
                if (value) {
                  const filteredProd = productos.filter((prod) =>
                    `${prod.nombre}`.toLowerCase().includes(value.toLowerCase())
                  );
                  setProductos(filteredProd);
                } else {
                  getProductos();
                }
              }}
            >
              {productos.map((producto) => (
                <Option key={producto.id} value={producto.nombre}>
                  {producto.nombre}
                </Option>
              ))}
            </Select>
          </Space>

          <Table
            dataSource={filteredInventario}
            columns={columns}
            rowKey="id"
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              pageSizeOptions: ["10", "25", "50"],
            }}
          />
          <Modal
            title="Historial del Producto"
            open={isModalVisible}
            onCancel={() => setIsModalVisible(false)}
            footer={null}
            width={800}
            styles={{
              body: {
                maxHeight: "60vh",
                overflowY: "auto",
                padding: "0 24px",
              },
            }}
          >
            {loading ? (
              <Spin tip="Cargando..." />
            ) : (
              <Tabs defaultActiveKey="prestamo">
                <Tabs.TabPane tab="Préstamo" key="prestamo">
                  <PrestamoTimeline historialData={historialData} />
                </Tabs.TabPane>
                <Tabs.TabPane tab="Factura" key="factura">
                  <FacturaList data={facturaData} />
                </Tabs.TabPane>
              </Tabs>
            )}
          </Modal>
        </>
      ) : (
        <h3>No tiene permisos para ver el inventario no serial</h3>
      )}
    </Layouts>
  );
};

export default InventarioNoSerialTable;
